import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COMING = [
  { glyph: "❋", title: "Cozy Blanket Pattern", note: "In testing" },
  { glyph: "✳", title: "Little Friend Pattern", note: "In testing" },
  { glyph: "❈", title: "Everyday Cardigan Pattern", note: "In testing" },
];

export default function Patterns() {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const IS_NETLIFY = process.env.REACT_APP_NETLIFY === "true";

  const join = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      if (IS_NETLIFY) {
        const res = await fetch("/", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams({ "form-name": "pattern-waitlist", "bot-field": "", email }).toString(),
        });
        if (!res.ok) throw new Error("Form submission failed");
      } else {
        const fd = new FormData();
        fd.append("email", email);
        await axios.post(`${API}/pattern-waitlist`, fd);
      }
      toast.success("You're on the list! I'll email you when patterns launch.");
      setEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong — please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="patterns" data-testid="patterns-section" className="bg-blend-mint py-20 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <Reveal className="text-center">
          <Eyebrow testId="patterns-eyebrow">Coming Soon</Eyebrow>
          <h2 className="mx-auto mt-4 max-w-2xl font-serif text-3xl font-medium leading-[1.15] tracking-tight text-[#2C2520] sm:text-4xl lg:text-5xl">
            Original patterns, <span className="italic text-[#3E8E82]">stitched by me, made by you.</span>
          </h2>
          <p data-testid="patterns-byline" className="mt-5 font-serif text-2xl font-medium italic tracking-tight text-[#2C2520]">
            Patterns by <span className="text-[#3E8E82]">Carol Elisabeth Smith</span>
          </p>
          <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-[#6E6259] sm:text-base">
            I&apos;m putting the finishing touches on my first written patterns — each one is being tested by
            real crocheters before it launches, so you know it works.
          </p>
          <p data-testid="patterns-disclaimer" className="mx-auto mt-4 max-w-xl text-xs italic leading-relaxed text-[#8C7A6B]">
            Some pieces are made using paid or free patterns by other talented designers, while others are my own
            originals — designers are always credited. Curious about a pattern? Just ask.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-6 sm:grid-cols-3 sm:gap-8">
          {COMING.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.1} className="h-full">
              <div data-testid={`pattern-card-${i}`} className="flex h-full flex-col items-center rounded-2xl border border-dashed border-[#C88A75]/40 bg-[#FDFBF7] p-8 text-center">
                <div className="stitch-grid flex h-24 w-24 items-center justify-center rounded-full border border-[#E6DFD5] bg-[#EFE6D8]/60">
                  <span className="font-serif text-4xl italic text-[#A35C45]">{c.glyph}</span>
                </div>
                <h3 className="mt-6 font-serif text-xl font-medium tracking-tight text-[#2C2520]">{c.title}</h3>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#8C7A6B]">{c.note}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15}>
          <form onSubmit={join} data-testid="pattern-waitlist-form" className="mx-auto mt-12 flex max-w-md flex-col gap-3 sm:flex-row">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              data-testid="pattern-waitlist-email-input"
              className="flex-1 rounded-full border border-[#E6DFD5] bg-[#FDFBF7] px-5 py-3.5 text-sm text-[#2C2520] outline-none transition-colors duration-300 placeholder:text-[#B4A794] focus:border-[#C88A75]"
            />
            <button
              type="submit"
              disabled={sending}
              data-testid="pattern-waitlist-submit-button"
              className="rounded-full bg-[#2C2520] px-6 py-3.5 text-sm font-medium text-[#FDFBF7] transition-colors duration-300 hover:bg-[#3E8E82] disabled:opacity-60"
            >
              {sending ? "Joining…" : "Notify me at launch"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
